import { ICONS } from "./icons.config.mjs";

/*
	"—" (em-dash) is the sentinel option prepended by scripts/sync-icons.mjs to
	every Prismic icon Select. Authors see "—" in the dropdown and read it as
	"no icon selected" — without it Prismic surfaces the first alphabetical
	option as the visible default and untouched fields save that value. When
	the value comes through as "—" or "" we render nothing silently. Any OTHER
	missing-entry lookup is a real bug, so we still warn in dev.
*/
const ICON_NONE_VALUES = new Set(["", "—"]);

/*
	Values that shipped in Prismic documents under older spellings, kept out of
	the Select options (sync-icons only publishes registry keys) but still
	resolved here so legacy content renders.
*/
const LEGACY_ALIASES = {
	Linkedin: "LinkedIn",
};

/**
 * Renders a named icon from the registry (components/Icon/icons.config.mjs).
 *
 * @param {object} props
 * @param {string} props.icon - Public icon name (the Prismic Select value).
 * @param {number|string} [props.size] - Width/height, passed to the glyph.
 * @param {string} [props.color] - Stroke/fill color (defaults to currentColor).
 * @param {string} [props.className]
 * @param {string} [props.label] - Accessible name. Omitted (the normal case)
 *   the icon is decorative and hidden from assistive tech; provided, the
 *   glyph becomes role="img" with this label.
 *
 * Remaining props (e.g. lucide's strokeWidth) pass through to the glyph.
 */
const Icon = ({ icon, size, color, className, label, ...rest }) => {
	if (!icon || ICON_NONE_VALUES.has(icon)) return null;
	const entry = ICONS[LEGACY_ALIASES[icon] || icon];
	if (!entry) {
		if (process.env.NODE_ENV !== "production") {
			console.warn(`[Icon] unknown icon "${icon}"`);
		}
		return null;
	}
	const { Component } = entry;
	const a11y = label
		? { role: "img", "aria-label": label }
		: { "aria-hidden": true, focusable: false };
	return (
		<Component
			size={size}
			color={color}
			className={className}
			{...a11y}
			{...rest}
		/>
	);
};

export default Icon;
